import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import { Plus, Megaphone, ClipboardText, X } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';

import { EmptyState } from '@/components/ui';
import { PollCard } from '@/components/features/PollCard/PollCard';
import { PollCardSkeleton } from '@/components/features/PollCardSkeleton/PollCardSkeleton';
import { FormCard } from '@/components/features/FormCard/FormCard';
import { FeedFilters } from '@/components/features/FeedFilters/FeedFilters';
import { usePolls, useForms } from '@/api';
import { useAuthStore } from '@/stores';
import { ROUTES } from '@/constants';

import type { PollFilters } from '@/types/poll';
import type { TypeFilter } from '@/components/features/FeedFilters/FeedFilters';

import styles from './Feed.module.css';

export function Feed() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { isLogged } = useAuthStore();
    const searchQuery = searchParams.get('search') || '';
    const [filters, setFilters] = useState<PollFilters>({
        ordering: '-hot_score',
    });
    const [myVotesActive, setMyVotesActive] = useState(false);
    const [activeType, setActiveType] = useState<TypeFilter>('all');
    const [fabMenuOpen, setFabMenuOpen] = useState(false);
    const fabMenuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        document.title = 'Ratio';
    }, []);

    // Close FAB menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (fabMenuRef.current && !fabMenuRef.current.contains(e.target as Node)) {
                setFabMenuOpen(false);
            }
        };
        if (fabMenuOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [fabMenuOpen]);

    // Polls are shown for 'all', 'poll', and 'quiz' tabs
    const showPolls = activeType !== 'form';
    const isQuizFilter = activeType === 'quiz' ? true : activeType === 'poll' ? false : undefined;

    const {
        data: pollsData,
        isLoading: pollsLoading,
        isFetchingNextPage: pollsFetchingNext,
        hasNextPage: pollsHasNext,
        fetchNextPage: pollsFetchNext,
    } = usePolls(
        {
            ...filters,
            voted: myVotesActive || undefined,
            search: searchQuery || undefined,
            is_quiz: isQuizFilter,
        },
        { enabled: showPolls }
    );

    // Forms are shown for 'all' and 'form' tabs
    const showForms = activeType === 'all' || activeType === 'form';

    const {
        data: formsData,
        isLoading: formsLoading,
        isFetchingNextPage: formsFetchingNext,
        hasNextPage: formsHasNext,
        fetchNextPage: formsFetchNext,
    } = useForms(
        {
            ordering: filters.ordering === '-hot_score' ? '-created' : filters.ordering,
            search: searchQuery || undefined,
        },
        { enabled: showForms }
    );

    const { ref: loadMoreRef } = useInView({
        onChange: (inView) => {
            if (!inView) return;
            if (pollsHasNext && !pollsFetchingNext) pollsFetchNext();
            if (formsHasNext && !formsFetchingNext) formsFetchNext();
        },
    });

    const handleFilterChange = useCallback((ordering: string) => {
        setFilters((prev) => ({ ...prev, ordering }));
        setMyVotesActive(false);
    }, []);

    const handleMyVotesToggle = useCallback(() => {
        setMyVotesActive((prev) => !prev);
    }, []);

    const handleTypeChange = useCallback((type: TypeFilter) => {
        setActiveType(type);
    }, []);

    const polls = pollsData?.pages.flatMap((page) => page.results) ?? [];
    const forms = formsData?.pages.flatMap((page) => page.results) ?? [];

    // Merge and sort polls + forms by created date for 'all' tab
    const feedItems = useMemo(() => {
        if (activeType === 'poll' || activeType === 'quiz') {
            return polls.map((p) => ({ type: 'poll' as const, item: p, created: p.created }));
        }
        if (activeType === 'form') {
            return forms.map((f) => ({ type: 'form' as const, item: f, created: f.created }));
        }
        // 'all': merge and sort by created desc
        const pollItems = polls.map((p) => ({ type: 'poll' as const, item: p, created: p.created }));
        const formItems = forms.map((f) => ({ type: 'form' as const, item: f, created: f.created }));
        return [...pollItems, ...formItems].sort(
            (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()
        );
    }, [activeType, polls, forms]);

    const isLoading = (showPolls && pollsLoading) || (showForms && formsLoading);
    const isFetchingNextPage = pollsFetchingNext || formsFetchingNext;
    const hasNextPage = pollsHasNext || formsHasNext;

    const emptyTitle = activeType === 'form' ? 'No hay formularios aun' : 'No hay encuestas aun';
    const emptyDescription = activeType === 'form' ? 'Crea el primer formulario!' : 'Crea la primera encuesta!';

    return (
        <div className={styles.page}>
            <div className={styles.filters}>
                <FeedFilters
                    activeFilter={filters.ordering || '-hot_score'}
                    onFilterChange={handleFilterChange}
                    showMyVotes={isLogged}
                    isMyVotesActive={myVotesActive}
                    onMyVotesToggle={handleMyVotesToggle}
                    activeType={activeType}
                    onTypeChange={handleTypeChange}
                />
            </div>

            {isLoading ? (
                <div className={styles.list}>
                    {[1, 2, 3].map((i) => (
                        <PollCardSkeleton key={i} />
                    ))}
                </div>
            ) : feedItems.length === 0 ? (
                <EmptyState
                    icon={<Megaphone weight="duotone" />}
                    title={emptyTitle}
                    description={emptyDescription}
                    actionLabel={isLogged ? (activeType === 'form' ? 'Crear formulario' : 'Crear encuesta') : 'Iniciar sesion'}
                    onAction={() => navigate(isLogged ? (activeType === 'form' ? ROUTES.createForm : ROUTES.create) : ROUTES.login)}
                />
            ) : (
                <AnimatePresence mode="popLayout">
                    <div className={styles.list}>
                        {feedItems.map((entry) => (
                            <motion.div
                                key={`${entry.type}-${entry.item.id}`}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.2 }}
                            >
                                {entry.type === 'poll' ? (
                                    <PollCard poll={entry.item} />
                                ) : (
                                    <FormCard form={entry.item} />
                                )}
                            </motion.div>
                        ))}
                    </div>
                </AnimatePresence>
            )}

            {/* Load more */}
            {hasNextPage && (
                <div ref={loadMoreRef} className={styles.loadMore}>
                    {isFetchingNextPage ? (
                        <span className={styles.loadMoreText}>Cargando...</span>
                    ) : (
                        <button
                            className={styles.loadMoreBtn}
                            onClick={() => { pollsFetchNext(); formsFetchNext(); }}
                            type="button"
                        >
                            Cargar mas
                        </button>
                    )}
                </div>
            )}

            {/* FAB with creation menu */}
            {isLogged && (
                <div className={styles.fabWrapper} ref={fabMenuRef}>
                    <AnimatePresence>
                        {fabMenuOpen && (
                            <motion.div
                                className={styles.fabMenu}
                                initial={{ opacity: 0, y: 10, scale: 0.9 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: 10, scale: 0.9 }}
                                transition={{ duration: 0.15 }}
                            >
                                <button
                                    type="button"
                                    className={styles.fabMenuItem}
                                    onClick={() => { setFabMenuOpen(false); navigate(ROUTES.create); }}
                                >
                                    <span className={styles.fabMenuIcon}>
                                        <Megaphone size={18} weight="duotone" />
                                    </span>
                                    <span className={styles.fabMenuLabel}>Encuesta rapida</span>
                                </button>
                                <button
                                    type="button"
                                    className={styles.fabMenuItem}
                                    onClick={() => { setFabMenuOpen(false); navigate(ROUTES.createForm); }}
                                >
                                    <span className={styles.fabMenuIcon}>
                                        <ClipboardText size={18} weight="duotone" />
                                    </span>
                                    <span className={styles.fabMenuLabel}>Formulario</span>
                                </button>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <motion.button
                        className={`${styles.fab} ${fabMenuOpen ? styles.fabOpen : ''}`}
                        onClick={() => setFabMenuOpen((v) => !v)}
                        aria-label={fabMenuOpen ? 'Cerrar menu' : 'Crear contenido'}
                        whileHover={{ scale: 1.08 }}
                        whileTap={{ scale: 0.92 }}
                    >
                        {fabMenuOpen ? <X size={24} weight="bold" /> : <Plus size={24} weight="bold" />}
                    </motion.button>
                </div>
            )}
        </div>
    );
}
